/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { Trip } from '../widgets/FlightCard/types';

export type BannerItem = {
  id: string;
  url: string;
  height: number;
  width: number;
  style: object;
};

export interface QueryType {
  query: string;
  geoPoint?: string;
  locale?: string;
  origin?: string;
}

export interface StationDataObject {
  StationType: string;
  Stations?: StationDataObject[];
  CityName: string;
  AirportName?: string;
  CountryName: string;
  StationCode: string;
  CityCode: string;
  CountryCode: string;
  StationName: string;
  TimeZone?: string;
  PinYin?: string;
  AlternativeName?: string;
  Tag?: string;
  Provider?: string;
  IsCity?: boolean;
  iconName?: string;
  Weightage?: number;
  Lat?: number | string;
  Long?: number | string;
  Dest?: string;
  destination?: StationDataObject[];
  AAFlight?: boolean;
  IsGroupedUnderCityNode?: boolean;
}

export type StationsSearchResponse = StationDataObject[];

export enum TripType {
  ROUND_TRIP = 'ROUNDTRIP',
  ONE_WAY = 'ONEWAY',
}

export type ApiPlatform = 'web' | 'android' | 'ios';

export type JourneyType = 'O' | 'R';

export type FunnelControlRequest = {
  searchContext: {
    flightJourney: {
      journeyType: JourneyType;
      journeyDetails: Array<{
        departDate: string;
        returnDate?: string;
        origin: string;
        destination: string;
        isOriginCity: boolean;
        isDestinationCity: boolean;
      }>;
      passengers: {
        adult: number;
        child: number;
        infant: number;
      };
    };
    promocode?: string | null;
    cabinClass: string;
  };
  userContext: {
    currency: string;
    geoId: string;
    locale: string;
    platform: ApiPlatform;
    ssoDetails?: {
      userId: string;
      accessToken?: string;
      refreshToken?: string;
    };
  };
  inventoryContext: {
    airlineProfile: string | null | undefined;
    type: string | null | undefined;
    provider: string;
  };
  metaData: {
    dest: string;
  };
  cartContext?: {
    cartId?: string;
    uiHide?: string;
    uiDisable?: string;
    airasiaFlightOnly?: boolean | string;
    sourceLob?: string;
    sourceDeeplink?: string;
  };
};

export type FunnelControlResponse = {
  redirectionUrl?: string;
  enableUCE: boolean;
  enableULE: boolean;
  provider: string;
  enableSnapUpsell: boolean;
  type: string;
  airlineProfile: string;
  enablePremiumFlatbedUpsell: boolean;
  enableFlightBusUpsell: boolean;
  enableAncillaryAbTest?: boolean;
};

export type SearchAggregatorFilters = {
  page?: number;

  airlineProfile: string;
  type: string;
  fareClass: string;

  enableSnapUpsell?: boolean;
  enablePremiumFlatbedUpsell?: boolean;
  enableUnifiedCheckout?: boolean;

  isOriginCity?: boolean;
  isDestinationCity?: boolean;

  departStation: string;
  arrivalStation: string;
  departDate: string;
  returnDate?: string | null;

  adult: number;
  child: number;
  infant: number;

  promocode?: string | null;
  sort?: string;

  consumerId?: string;
  selectedDepartFlight?: Trip;
};

export type SearchAggregatorResponse = any;

export interface SearchAggregatorDeeplinkResponse {
  bundledTripId: string;
  bundledFlights: boolean;
  redirect: boolean;
  enableUCE: boolean;
  redirectionUrl: string;
  experimentVariant: string | null;
  unifiedAncillary: boolean;
  variantId: string | null;
  airlineProfile: string;
  departCabinType: string;
  returnCabinType?: string;
  selectedBundleIds?: Array<string | null>;
}

export interface refreshSearchParams {
  sessionTimeOut: boolean;
  refreshSearch: boolean;
}

export type SSODetails = {
  accessToken: string;
  refreshToken: string;
  userId: string;
};

export type UserContext = {
  currency: string;
  geoId: string;
  locale: string;
  platform: ApiPlatform;
  version?: string;
};

type BaseHeaders = {
  Accept: 'application/json';
  'Content-Type': 'application/json';
  channel_hash: string;
};

export type FunnelHeadersInput = BaseHeaders & {
  'Ga-Id'?: string;
};

export type SearchAggregatorInput = BaseHeaders & {
  'User-Type': string;
  Authorization: string;
};

export type SearchAggregatorDeeplinkRequest = BaseHeaders & {
  'User-Type': string;
  Authorization: string;
};

export type PipelineResult = {
  funnel: FunnelControlResponse;
  aggregator: SearchAggregatorResponse | null;
};

export type SearchAggregatorVars = {
  shouldCallFunnel?: boolean;
  shouldCallAggregator?: boolean;
  locale?: string;
  currency?: string;
  showSoldOutFlights?: boolean;
  funnelData?: Omit<FunnelControlResponse, 'redirectionUrl'>;
};

export type CrossSell = {
  position?: number;
  type?: string;
  widgetName: string;
  subtitle: string;
  title: string;
  cta: string;
  url: string;
  image: string;
  status: string;
  newTab: boolean;
  mobileAppImage: string;
  widgetID: number;
  redirectionType: string;
  customAttributes?: CustomAttributes;
};

export type CrossSellBanner = CrossSell & {
  header: string;
  description?: string;
  btnCta?: string;
  showTertiaryInfo?: boolean;
};

export type CustomAttributes = {
  destination?: string;
  origin?: string;
  isOC?: string;
  isDC?: string;
};

export type Passengers = {
  adult: number;
  child: number;
  infant: number;
};

export type ListingQueryParams = {
  orig?: string;
  dest?: string;
  fromDateFlight?: string;
  toDateFlight?: string;
  adults?: number;
  children?: number[];
  infants?: number[];
  language?: string;
  currency?: string;
  country_code?: string;
  rooms?: number;
  promoCode?: string;
};
